import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CompetencyMatrixForm from "../components/fase2/CompetencyMatrixForm";
import Header from "../components/Header";
import { fetchV2, tieneTokenV2 } from "../utils/apiV2";
import "./EvaluacionFase2.css";

/**
 * Evaluación Fase 2 en producción: el instrumento se resuelve por el
 * Nivel organizacional / tipo de evaluación del cargo del empleado
 * (GET api/v2/empleados/{id}/instrumento). No usa los 23 aspectos legacy.
 *
 * Query:
 *   ?fuente=auto|jefe  (default auto)
 *   ?empleadoId=N      (evaluado; default = usuario logueado)
 */
export default function EvaluacionFase2({ onLogout, userRole }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fuenteParam = searchParams.get("fuente") || "auto";
  const fuente = ["auto", "jefe", "par", "colaborador"].includes(fuenteParam)
    ? fuenteParam
    : "auto";

  const loggedEmployeeId = localStorage.getItem("employeeId") || "";
  const empleadoId = searchParams.get("empleadoId") || loggedEmployeeId;

  const [estadoCarga, setEstadoCarga] = useState("cargando");
  const [mensajeError, setMensajeError] = useState("");
  const [instrumento, setInstrumento] = useState(null);
  const [idAsignacion, setIdAsignacion] = useState(null);
  const [mensajeAsignacion, setMensajeAsignacion] = useState("");
  const [estadoFormulario, setEstadoFormulario] = useState(null);
  const [envioEstado, setEnvioEstado] = useState("idle");
  const [envioMensaje, setEnvioMensaje] = useState("");

  useEffect(() => {
    let cancelado = false;

    async function cargar() {
      if (!tieneTokenV2()) {
        setEstadoCarga("sin-token");
        setMensajeError(
          "Tu sesión no tiene token de autenticación. Cierra sesión e inicia de nuevo para continuar con la evaluación Fase 2."
        );
        return;
      }

      if (!empleadoId) {
        setEstadoCarga("error");
        setMensajeError("No se encontró el ID del empleado a evaluar.");
        return;
      }

      setEstadoCarga("cargando");
      setMensajeError("");
      setInstrumento(null);
      setIdAsignacion(null);
      setMensajeAsignacion("");

      const inst = await fetchV2(`api/v2/empleados/${encodeURIComponent(empleadoId)}/instrumento`);
      if (cancelado) return;

      if (!inst.ok) {
        if (inst.status === 401 || inst.status === 403) {
          setEstadoCarga("sin-autorizacion");
          setMensajeError(
            (inst.data && inst.data.message) ||
              "No tienes permiso para ver este instrumento."
          );
        } else {
          setEstadoCarga("error");
          setMensajeError(
            (inst.data && inst.data.message) ||
              "No se pudo resolver el instrumento de evaluación para este empleado."
          );
        }
        return;
      }

      setInstrumento(inst.data.data);

      const asig = await fetchV2(
        `api/v2/asignaciones/para-evaluar?id_empleado=${encodeURIComponent(empleadoId)}&fuente=${encodeURIComponent(fuente)}`
      );
      if (cancelado) return;

      if (asig.ok && asig.data && asig.data.data) {
        setIdAsignacion(asig.data.data.id_asignacion);
        setMensajeAsignacion("");
      } else {
        setIdAsignacion(null);
        setMensajeAsignacion(
          (asig.data && asig.data.message) ||
            "No hay una asignación pendiente. Un administrador debe generar las evaluaciones del período activo antes de poder guardar."
        );
      }

      setEstadoCarga("listo");
    }

    cargar();
    return () => {
      cancelado = true;
    };
  }, [empleadoId, fuente]);

  async function enviarCalificaciones() {
    if (!idAsignacion || !estadoFormulario) return;

    setEnvioEstado("enviando");
    setEnvioMensaje("");

    const pendientes = estadoFormulario.calificaciones.filter((c) => c.valor);
    let errores = 0;

    for (const calificacion of pendientes) {
      const { ok, data } = await fetchV2(
        `api/v2/asignaciones/${idAsignacion}/calificaciones`,
        {
          method: "POST",
          body: JSON.stringify({
            id_comportamiento: calificacion.id_comportamiento,
            valor: calificacion.valor,
            justificacion: calificacion.justificacion || null,
            evidencia_texto: calificacion.evidenciaTexto || null,
          }),
        }
      );
      if (!ok) {
        errores += 1;
        setEnvioMensaje((prev) =>
          `${prev}\n${(data && data.message) || "Error desconocido"}`.trim()
        );
      }
    }

    if (errores === 0 && estadoFormulario.completo) {
      const cierre = await fetchV2(
        `api/v2/asignaciones/${idAsignacion}/completar`,
        { method: "POST", body: JSON.stringify({}) }
      );
      if (!cierre.ok) {
        errores += 1;
        setEnvioMensaje((prev) =>
          `${prev}\n${(cierre.data && cierre.data.message) || "No se pudo completar la asignación"}`.trim()
        );
      }
    }

    setEnvioEstado(errores === 0 ? "ok" : "error");
    if (errores === 0) {
      setEnvioMensaje(
        `Se guardaron ${pendientes.length} calificación(es)${
          estadoFormulario.completo ? " y se marcó la asignación como completada" : ""
        }.`
      );
    }
  }

  const tituloFuente =
    fuente === "auto"
      ? "Autoevaluación"
      : fuente === "jefe"
        ? "Evaluación del jefe"
        : `Evaluación (${fuente})`;

  return (
    <div className="evaluacion-fase2-layout">
      <Header onLogout={onLogout} userRole={userRole} />
      <div className="evaluacion-fase2-page">
        <div className="evaluacion-fase2-header">
          <button
            type="button"
            className="evaluacion-fase2-volver"
            onClick={() => navigate("/LandingPage")}
          >
            ← Volver
          </button>
          <h1>{tituloFuente} — Fase 2</h1>
          <p className="evaluacion-fase2-nota">
            El cuestionario se carga según el Nivel organizacional / tipo de
            evaluación del cargo del empleado.
          </p>
        </div>

        {estadoCarga === "cargando" && (
          <div className="evaluacion-fase2-estado">Cargando instrumento…</div>
        )}

        {(estadoCarga === "error" ||
          estadoCarga === "sin-token" ||
          estadoCarga === "sin-autorizacion") && (
          <div className="evaluacion-fase2-estado evaluacion-fase2-error" role="alert">
            <p>{mensajeError}</p>
            {estadoCarga === "sin-token" && (
              <button type="button" onClick={() => onLogout && onLogout()}>
                Cerrar sesión
              </button>
            )}
          </div>
        )}

        {estadoCarga === "listo" && instrumento && (
          <>
            <div className="evaluacion-fase2-meta">
              <div>
                <span className="evaluacion-fase2-meta-label">Nivel / tipo</span>
                <strong>
                  {instrumento.nombre_banco || instrumento.codigo_banco}
                  {instrumento.codigo_banco
                    ? ` (${instrumento.codigo_banco})`
                    : ""}
                </strong>
              </div>
              <div>
                <span className="evaluacion-fase2-meta-label">Empleado ID</span>
                <strong>{empleadoId}</strong>
              </div>
              <div>
                <span className="evaluacion-fase2-meta-label">Fuente</span>
                <strong>{fuente}</strong>
              </div>
            </div>

            {mensajeAsignacion && (
              <div className="evaluacion-fase2-aviso" role="status">
                {mensajeAsignacion}
              </div>
            )}

            <CompetencyMatrixForm
              codigoBanco={instrumento.codigo_banco}
              fuente={fuente}
              onCambio={setEstadoFormulario}
            />

            {estadoFormulario && (
              <div className="evaluacion-fase2-acciones">
                <p>
                  {estadoFormulario.totalRespondidos} /{" "}
                  {estadoFormulario.totalRequeridos} comportamientos respondidos
                  {estadoFormulario.completo ? " — completo" : ""}.
                </p>

                {idAsignacion ? (
                  <>
                    <button
                      type="button"
                      className="evaluacion-fase2-guardar"
                      onClick={enviarCalificaciones}
                      disabled={
                        envioEstado === "enviando" ||
                        estadoFormulario.totalRespondidos === 0
                      }
                    >
                      {envioEstado === "enviando"
                        ? "Guardando…"
                        : "Guardar calificaciones"}
                    </button>
                    {envioMensaje && (
                      <pre
                        className={`evaluacion-fase2-envio evaluacion-fase2-envio-${envioEstado}`}
                      >
                        {envioMensaje}
                      </pre>
                    )}
                  </>
                ) : (
                  <p className="evaluacion-fase2-sin-guardar">
                    Puedes revisar el cuestionario, pero no se puede guardar
                    hasta que exista una asignación del período activo.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
